import {
    SketchTrimCurveKind,
    SketchTrimMode,
    SketchTrimControllerStage,
} from '../SketchTrimTypes';
import type {
    SketchTrimCurveObject, SketchTrimEntityRef, SketchTrimOperationResult,
    SketchTrimPowerMarkerSnapshot, SketchTrimPowerUndoRecord, SketchTrimUpdatedCurveSnapshot,
    SketchTrimDragState,
} from '../SketchTrimTypes';
import type {SketchTrimModeService} from './SketchTrimModeServiceTypes';
import type {SketchTrimController} from '../SketchTrimController';
import type {RawPointerEventPayload, SketchRuntimeHistorySession} from '../../../viewer';
import type {SketchTrimDimensionStateSnapshot} from '../../../../types/sketch/trim/SketchTrimDimensionIntegrationTypes';
import type {SketchTrimPowerExtendUndo} from '../../../../types/sketch/trim/SketchTrimPowerExtendTypes';
import {SketchTrimPowerExtendService} from '../extend';
import type {Object3D} from 'three';

/**
 * Power 模式服务。
 *
 * 职责：
 * 1. 管理强力剪裁拖拽态；
 * 2. 处理单击与拖拽采样；
 * 3. 在 Power 工作流中控制点击抑制与撤销态重置。
 */
export class SketchTrimPowerModeService implements SketchTrimModeService {
    /** 当前服务绑定的模式。 */
    public readonly mode = SketchTrimMode.Power;

    /** 最近一次拖拽采样点。 */
    private dragState: SketchTrimDragState | null = null;
    /** 当前 Shift 手势；几何只作用于手势开始时锁定的实体。 */
    private activeExtension: SketchTrimPowerExtendService | null = null;
    /** 当前连续 Shift 区段是否已经尝试锁定目标，防止空白拖动横扫实体。 */
    private extensionAttempted = false;
    /** 接管拖拽前的相机交互状态，SDK 独立使用时也不能同时平移视图。 */
    private cameraEnabledBefore: boolean | null = null;
    /** 鼠标抬起后的点击抑制标记。 */
    private suppressNextClick = false;
    /** 强力剪裁撤销记录栈。 */
    private readonly powerTrimUndoRecordStack: (SketchTrimPowerUndoRecord | SketchTrimPowerExtendUndo)[] = [];
    /** 强力剪裁标记快照栈。 */
    private readonly powerTrimMarkerSnapshotStack: SketchTrimPowerMarkerSnapshot[] = [];
    /** 强力剪裁撤销版本号。 */
    private powerTrimUndoToken = 0;
    /** 当前拖拽级草图历史会话。 */
    private dragHistorySession: SketchRuntimeHistorySession | null = null;
    /** 当前拖拽开始前的尺寸仓库状态。 */
    private dragDimensionStateBefore: SketchTrimDimensionStateSnapshot | null = null;
    /** 所属剪裁控制器。 */
    private readonly controller: SketchTrimController;

    /**
     * 构造函数。
     * @param controller 剪裁控制器。
     */
    public constructor(controller: SketchTrimController) {
        this.controller = controller;
    }

    /**
     * 获取模式显示文案。
     * @returns 文案。
     */
    public getDisplayName(): string {
        return '强力剪裁（Power）';
    }

    /**
     * 声明 Power 的曲线准入规则，供悬停和点击选择共用。
     *
     * 缺少此声明时选择管理器会退回直线规则，导致可执行裁剪的圆弧无法显示悬停反馈。
     * @param object 待选择对象。
     * @returns 正式曲线满足当前构造几何及可见性选项时返回 true。
     */
    public isSelectableEntity(object: Object3D): boolean {
        return this.controller.serviceIsTrimCurveSelectableEntity(object);
    }

    /**
     * 获取当前可显示的强力剪裁标记快照。
     * @returns 最近记录属于剪裁时返回标记；Shift、延伸区段或延伸记录返回 null。
     */
    public getLastPowerTrimMarkerSnapshot(): SketchTrimPowerMarkerSnapshot | null {
        const latestRecord = this.powerTrimUndoRecordStack[this.powerTrimUndoRecordStack.length - 1];
        // 延伸的历史能力与剪裁方块是不同契约；不能显示旧剪裁方块却回退较新的延伸记录。
        if (this.extensionAttempted || this.controller.getViewer?.()?.eventManager?.isShiftPressed
            || (latestRecord && 'undo' in latestRecord)) {
            return null;
        }
        const lastMarkerSnapshot = this.powerTrimMarkerSnapshotStack[this.powerTrimMarkerSnapshotStack.length - 1] ?? null;
        if (!lastMarkerSnapshot) {
            return null;
        }
        return {
            undoToken: lastMarkerSnapshot.undoToken,
            worldPoint: [...lastMarkerSnapshot.worldPoint] as [number, number, number],
        };
    }

    /**
     * 撤销最近一次强力剪裁操作。
     * @returns 是否成功撤销。
     */
    public undoLastPowerTrim(): boolean {
        const record = this.powerTrimUndoRecordStack.pop() ?? null;
        if (!record) {
            return false;
        }

        if ('undo' in record) {
            // 延伸与裁剪保持同一回退次序，回退后不继续使用旧的鼠标抓取定义。
            record.undo();
            this.activeExtension = null;
            this.powerTrimMarkerSnapshotStack.pop();
            return true;
        }

        const createdLineIds = [...record.createdLineIds].reverse();
        createdLineIds.forEach((createdLineId) => {
            const createdLine = this.controller.serviceResolvePowerTrimCurveById(createdLineId);
            if (!createdLine) {
                return;
            }
            this.controller.serviceRemoveCurveEntity(createdLine);
        });

        record.removedLineSnapshots.forEach((removedLineSnapshot) => {
            this.controller.serviceRestoreRemovedCurve(removedLineSnapshot);
        });
        record.updatedLineSnapshots.forEach((lineSnapshot) => {
            this.controller.serviceRestoreCurveSnapshot(lineSnapshot);
        });

        // 红方块撤销属于命令内逐步回退，尺寸仓库必须与几何快照恢复到同一个版本。
        this.controller.serviceRestoreSketchDimensionStateIfCurrent(
            record.dimensionStateAfter,
            record.dimensionStateBefore,
        );

        this.powerTrimMarkerSnapshotStack.pop();
        this.controller.getViewer().requestRender();
        this.controller.getViewer().drawingNoticeCallback?.('已还原上一次强力剪裁。', 'info');
        return true;
    }

    /**
     * 清理强力剪裁撤销态。
     */
    public clearPowerUndoState(): void {
        this.cancelDragHistorySession();
        this.releaseExtension();
        this.restoreCameraControls();
        this.powerTrimUndoRecordStack.forEach(record => {
            if ('release' in record) record.release();
        });
        this.powerTrimUndoRecordStack.length = 0;
        this.powerTrimMarkerSnapshotStack.length = 0;
    }

    /**
     * 执行强力剪裁单实体操作。
     * @param target 目标实体。
     * @returns 操作结果。
     */
    public executeSingleEntityOperation(target: SketchTrimEntityRef): SketchTrimOperationResult {
        const powerUndoRecord = this.capturePowerUndoRecordBeforeOperation(target);
        try {
            const result = this.applyPowerTrimOperation(target);
            if (result.success) {
                // 几何变更完成后立即迁移尺寸仓库，再捕获命令内撤销的统一终点。
                this.controller.serviceApplyDimensionTopologyChanges(result.topologyChanges ?? []);
                if (!this.controller.getViewer().solveActiveSketchParametricTrimOperation()) {
                    console.error('[SketchTrimPowerModeService] 强力裁剪后的完整草图约束无法满足。');
                    this.rollbackFailedPowerOperation(powerUndoRecord, result);
                    return {
                        success: false,
                        message: '强力裁剪后的完整草图约束无法满足。',
                        createdEntityIds: [],
                        removedEntityIds: [],
                    };
                }
                powerUndoRecord.dimensionStateAfter = this.controller.serviceCaptureActiveSketchDimensionState();
                this.commitPowerUndoRecord(powerUndoRecord, result, target);
            }
            return result;
        } catch (error) {
            // 尺寸迁移异常时先恢复领域仓库，外层历史事务负责恢复同一笔几何快照。
            this.controller.serviceRestoreSketchDimensionState(powerUndoRecord.dimensionStateBefore);
            console.error(`[SketchTrimPowerModeService] 强力裁剪单实体执行失败：${error instanceof Error ? error.message : String(error)}`);
            throw error;
        }
    }

    /**
     * 回滚未通过完整草图参数求解的 Power 候选操作。
     *
     * 说明：
     * 1. 单击 Power 外层通常还有草图历史事务，但拖拽中的单实体操作直接运行在拖拽会话内；
     * 2. 求解失败改为显式失败返回后，必须在本服务内同步恢复本次几何和尺寸，避免拖拽继续保留无效候选；
     * 3. 回滚不写入红方块撤销栈，也不生成历史终点。
     *
     * @param undoRecord 操作前捕获的几何与尺寸快照。
     * @param result 已产生的候选裁剪结果。
     */
    private rollbackFailedPowerOperation(
        undoRecord: SketchTrimPowerUndoRecord,
        result: SketchTrimOperationResult,
    ): void {
        // 先移除本次创建对象，避免恢复源对象后出现重复曲线。
        [...result.createdEntityIds].reverse().forEach((createdEntityId) => {
            const createdCurve = this.controller.serviceResolvePowerTrimCurveById(createdEntityId);
            if (createdCurve) {
                this.controller.serviceRemoveCurveEntity(createdCurve);
            }
        });

        // 只恢复本次真实删除的源曲线，防止无效候选误挂载未删除快照。
        const removedEntityIds = new Set(result.removedEntityIds);
        undoRecord.removedLineSnapshots
            .filter((snapshot) => removedEntityIds.has(snapshot.line.uuid))
            .forEach((snapshot) => {
                this.controller.serviceRestoreRemovedCurve(snapshot);
            });
        undoRecord.updatedLineSnapshots.forEach((snapshot) => {
            this.controller.serviceRestoreCurveSnapshot(snapshot);
        });

        this.controller.serviceRestoreSketchDimensionState(undoRecord.dimensionStateBefore);
        this.controller.getViewer().requestRender();
    }

    /**
     * 重置模式私有状态。
     * @param context 服务上下文。
     */
    public resetState(): void {
        this.cancelDragHistorySession();
        this.releaseExtension();
        this.restoreCameraControls();
        this.dragState = null;
        this.suppressNextClick = false;
        this.controller.serviceSetLastSelectedEntity(null);
    }

    /** 指针取消时恢复拖拽 before，并清理所有命令内回退引用。 */
    public handlePointerCancel(): void {
        this.clearPowerUndoState();
        this.dragState = null;
        this.suppressNextClick = true;
        this.controller.serviceSetStage(SketchTrimControllerStage.Armed);
    }

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     * @param context 服务上下文。
     * @returns 是否已消费。
     */
    public handleMouseDown(payload: RawPointerEventPayload): boolean {
        if (payload.button !== 0) {
            return false;
        }

        //------ Trim 五模式服务拆分 开始------
        // 新一轮强力剪裁拖拽开始时，必须立即废弃上一轮撤销栈与红方块标记，
        // 否则覆盖层会把上一轮的回退态错误带入当前拖拽。
        this.controller.serviceClearPowerUndoState();
        //------ Trim 五模式服务拆分 结束------
        this.beginDragHistorySession();
        const cameraManager = this.controller.getViewer?.().cameraManager;
        if (cameraManager) {
            this.cameraEnabledBefore = cameraManager.controls?.enabled ?? true;
            cameraManager.setControlsEnabled(false);
        }
        this.controller.serviceSetStage(SketchTrimControllerStage.Dragging);
        this.dragState = {
            lastClientX: payload.clientX,
            lastClientY: payload.clientY,
        };
        this.suppressNextClick = false;
        if (payload.shiftKey) {
            try {
                this.beginExtension(payload.clientX, payload.clientY);
            } catch (error) {
                this.handlePointerCancel();
                console.error(`[SketchTrimPowerModeService] 强力延伸开始失败：${error instanceof Error ? error.message : String(error)}`);
            }
        }
        return true;
    }

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     * @param context 服务上下文。
     * @returns 是否已消费。
     */
    public handleMouseMove(payload: RawPointerEventPayload): boolean {
        if (this.controller.serviceGetStage() !== SketchTrimControllerStage.Dragging) {
            return false;
        }
        if (!this.dragState) {
            console.error(`[SketchTrimPowerModeService] 剪裁拖拽状态异常：缺少 dragState。`);
            this.cancelDragHistorySession();
            this.controller.serviceSetStage(SketchTrimControllerStage.Armed);
            return true;
        }

        const fromX = this.dragState.lastClientX;
        const fromY = this.dragState.lastClientY;
        const toX = payload.clientX;
        const toY = payload.clientY;
        if (fromX === toX && fromY === toY) {
            return true;
        }

        try {
            this.applyDragTargetsAlongSegment(fromX, fromY, toX, toY, Boolean(payload.shiftKey));
            this.dragState.lastClientX = toX;
            this.dragState.lastClientY = toY;
        } catch (error) {
            this.cancelDragHistorySession();
            this.clearPowerUndoState();
            this.controller.serviceSetStage(SketchTrimControllerStage.Armed);
            this.dragState = null;
            console.error(`[SketchTrimPowerModeService] 强力剪裁拖拽处理失败：${error instanceof Error ? error.message : String(error)}`);
        }
        return true;
    }

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     * @param context 服务上下文。
     * @returns 是否已消费。
     */
    public handleMouseUp(payload: RawPointerEventPayload): boolean {
        if (payload.button !== 0) {
            return false;
        }
        if (this.controller.serviceGetStage() === SketchTrimControllerStage.Dragging) {
            try {
                if (this.dragState) {
                    const fromX = this.dragState.lastClientX;
                    const fromY = this.dragState.lastClientY;
                    const toX = payload.clientX;
                    const toY = payload.clientY;
                    const distance = Math.hypot(toX - fromX, toY - fromY);

                    if (distance > 0) {
                        this.applyDragTargetsAlongSegment(fromX, fromY, toX, toY, Boolean(payload.shiftKey));
                    }
                }
                this.commitDragHistorySession();
            } catch (error) {
                this.cancelDragHistorySession();
                this.clearPowerUndoState();
                console.error(`[SketchTrimPowerModeService] 强力剪裁鼠标抬起处理失败：${error instanceof Error ? error.message : String(error)}`);
            } finally {
                this.releaseExtension();
                this.restoreCameraControls();
                this.controller.serviceSetStage(SketchTrimControllerStage.Armed);
                this.dragState = null;
                this.suppressNextClick = true;
            }
        }
        return true;
    }

    /**
     * 开启拖拽级历史会话。
     */
    private beginDragHistorySession(): void {
        this.cancelDragHistorySession();
        this.dragDimensionStateBefore = this.controller.serviceCaptureActiveSketchDimensionState();
        const viewer = this.controller.getViewer?.();
        if (!viewer?.beginActiveSketchRuntimeHistorySession) {
            this.dragHistorySession = null;
            return;
        }
        this.dragHistorySession = viewer.beginActiveSketchRuntimeHistorySession({
            label: '强力裁剪草图实体',
            commandType: 'sketch.runtime.trim.power-drag',
        });
    }

    /**
     * 提交拖拽级历史会话。
     */
    private commitDragHistorySession(): void {
        const historySession = this.dragHistorySession;
        const dimensionStateBefore = this.dragDimensionStateBefore;
        const dimensionStateAfter = this.controller.serviceCaptureActiveSketchDimensionState();
        const dimensionPatches = this.controller.serviceCreateSketchDimensionHistoryPatches(
            dimensionStateBefore,
            dimensionStateAfter,
        );
        this.dragHistorySession = null;
        this.dragDimensionStateBefore = null;
        void historySession?.commit({extraPatches: dimensionPatches}).catch((error) => {
            // 异步提交失败只能在仓库仍处于本次终点时回滚，避免覆盖随后发生的新编辑。
            this.controller.serviceRestoreSketchDimensionStateIfCurrent(
                dimensionStateAfter,
                dimensionStateBefore,
            );
            console.error(`[SketchTrimPowerModeService] 强力裁剪草图历史提交失败：${error instanceof Error ? error.message : String(error)}`);
        });
    }

    /**
     * 取消拖拽级历史会话。
     */
    private cancelDragHistorySession(): void {
        const historySession = this.dragHistorySession;
        const dimensionStateBefore = this.dragDimensionStateBefore;
        this.dragHistorySession = null;
        this.dragDimensionStateBefore = null;
        historySession?.cancel();

        // 会话取消已同步恢复几何，随后恢复尺寸仓库，保证运行时关系视图基于恢复后的草图重建。
        if (dimensionStateBefore) {
            this.controller.serviceRestoreSketchDimensionState(dimensionStateBefore);
        }
    }

    /**
     * 处理点击。
     * @param payload 原始事件。
     * @param context 服务上下文。
     * @returns 是否已消费。
     */
    public handleClick(payload: RawPointerEventPayload): boolean {
        if (payload.button !== 0) {
            return false;
        }
        if (this.suppressNextClick) {
            this.suppressNextClick = false;
            return true;
        }
        // Shift 的实体点击仅建立延伸手势，绝不能回落到删除实体的单击分支。
        if (payload.shiftKey) return true;

        const picked = this.controller.servicePickTrimCurveAtClientPoint(
            payload.clientX,
            payload.clientY,
            this.controller.serviceGetOptions().ignoreConstructionGeometry,
        );
        if (!picked) {
            return true;
        }

        const targetEntity: SketchTrimEntityRef = {
            objectId: picked.objectId,
            segmentIndex: picked.segmentIndex,
            hitPoint: picked.hitPoint,
        };
        this.controller.serviceSetLastSelectedEntity(this.controller.serviceCloneTrimEntityRef(targetEntity));
        this.controller.serviceApplySingleEntityOperation(this.mode, targetEntity, false);
        return true;
    }

    /**
     * 执行 Power 模式。
     * @param target 目标实体。
     * @returns 操作结果。
     */
    private applyPowerTrimOperation(target: SketchTrimEntityRef): SketchTrimOperationResult {
        const curve = this.controller.serviceResolvePowerTrimTarget(target);
        if (!curve) {
            return {
                success: false,
                message: '未找到可操作曲线。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        const curveData = this.controller.serviceResolveCurveData(curve, target.segmentIndex ?? null);

        const pickPoint = this.controller.serviceResolveCurvePickPoint(curveData, target.hitPoint);
        const pickT = this.controller.serviceProjectPointToCurveParameter(curveData, pickPoint);
        const boundaries = this.controller.serviceCollectBoundaryParametersForPowerCurve(curveData);

        if (
            boundaries.length === 0
            && (
                curveData.curveKind === SketchTrimCurveKind.Circle
                || (curveData.curveKind === SketchTrimCurveKind.Ellipse && curveData.isClosed)
            )
        ) {
            return this.applyWholeCurvePowerTrim(curveData.curve, '孤立闭合曲线已删除。');
        }

        const trimInterval = curveData.isClosed
            ? this.controller.serviceResolveClosedTrimIntervalByPick(boundaries, pickT)
            : this.controller.serviceResolveTrimIntervalByPick(boundaries, pickT);
        if (!trimInterval) {
            return {
                success: false,
                message: '未找到可剪裁区段。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        return this.controller.serviceApplyCurveTrimInterval(curveData, trimInterval);
    }

    /**
     * 执行整曲线删除形式的强力剪裁。
     * @param curve 目标曲线。
     * @param message 操作成功文案。
     * @returns 操作结果。
     */
    private applyWholeCurvePowerTrim(
        curve: SketchTrimCurveObject,
        message: string,
    ): SketchTrimOperationResult {
        const removalResult = this.controller.serviceRemoveCurveEntityWithTopology(curve);
        this.controller.getViewer().requestRender();
        return {
            success: true,
            message,
            createdEntityIds: [],
            removedEntityIds: [...removalResult.removedEntityIds],
            topologyChanges: [...removalResult.topologyChanges],
        };
    }

    /**
     * 对一段拖拽路径执行采样拾取并立即应用修剪。
     * @param fromX 起点 X。
     * @param fromY 起点 Y。
     * @param toX 终点 X。
     * @param toY 终点 Y。
     * @param extend 是否按住 Shift 进行自然路径延伸。
     */
    private applyDragTargetsAlongSegment(
        fromX: number,
        fromY: number,
        toX: number,
        toY: number,
        extend: boolean,
    ): void {
        if (extend) {
            if (!this.extensionAttempted) this.beginExtension(fromX, fromY);
            const extension = this.activeExtension;
            if (!extension) return;
            const wasChanged = extension.changed;
            if (extension.update(toX, toY) && !wasChanged) {
                // 每次连续延伸只登记一次回退记录，mousemove 不堆积快照或模型事务。
                this.powerTrimUndoRecordStack.push(extension);
                this.powerTrimUndoToken += 1;
                this.powerTrimMarkerSnapshotStack.push({
                    undoToken: this.powerTrimUndoToken,
                    worldPoint: [...extension.target!.hitPoint!] as [number, number, number],
                });
            }
            return;
        }
        if (this.extensionAttempted) {
            // 松开 Shift 的过渡段只重置抓取，不把刚延伸的路径立即当作裁剪轨迹。
            this.releaseExtension();
            return;
        }
        const targets = this.controller.servicePickSketchTrimEntitiesAlongClientSegment(
            this.mode,
            fromX,
            fromY,
            toX,
            toY,
            this.controller.serviceGetOptions().ignoreConstructionGeometry,
        );
        targets.forEach((target) => {
            this.controller.serviceApplySingleEntityOperation(this.mode, target, true);
        });
    }

    /**
     * 在一个 Shift 区段的起点锁定实体。
     * @param clientX 起点客户端 X。
     * @param clientY 起点客户端 Y。
     */
    private beginExtension(clientX: number, clientY: number): void {
        this.extensionAttempted = true;
        const extension = new SketchTrimPowerExtendService(this.controller);
        try {
            if (extension.begin(clientX, clientY)) this.activeExtension = extension;
            else extension.release();
        } catch (error) {
            extension.release();
            throw error;
        }
    }

    /** 结束当前抓取；已变更的手势继续由命令内回退栈持有。 */
    private releaseExtension(): void {
        if (this.activeExtension && !this.activeExtension.changed) this.activeExtension.release();
        this.activeExtension = null;
        this.extensionAttempted = false;
    }

    /** 所有完成、取消和异常出口恢复接管前的相机状态，UI 不再重复管理。 */
    private restoreCameraControls(): void {
        if (this.cameraEnabledBefore === null) return;
        this.controller.getViewer().cameraManager?.setControlsEnabled(this.cameraEnabledBefore);
        this.cameraEnabledBefore = null;
    }

    /**
     * 在执行操作前捕获撤销记录。
     * @param target 目标实体。
     * @returns 撤销记录。
     */
    private capturePowerUndoRecordBeforeOperation(target: SketchTrimEntityRef): SketchTrimPowerUndoRecord {
        const dimensionStateBefore = this.controller.serviceCaptureActiveSketchDimensionState();
        const curve = this.controller.serviceResolvePowerTrimTarget(target);
        if (!curve) {
            return {
                updatedLineSnapshots: [],
                removedLineSnapshots: [],
                createdLineIds: [],
                dimensionStateBefore,
                dimensionStateAfter: dimensionStateBefore,
            };
        }
        const updatedLineSnapshot = this.controller.serviceCaptureCurveSnapshot(curve);
        const removedLineSnapshot = this.controller.serviceCaptureRemovedCurveSnapshot(curve);
        return {
            updatedLineSnapshots: updatedLineSnapshot ? [updatedLineSnapshot] : [],
            removedLineSnapshots: removedLineSnapshot ? [removedLineSnapshot] : [],
            createdLineIds: [],
            dimensionStateBefore,
            dimensionStateAfter: dimensionStateBefore,
        };
    }

    /**
     * 提交强力剪裁撤销记录。
     * @param undoRecord 撤销记录。
     * @param result 操作结果。
     * @param target 目标实体。
     */
    private commitPowerUndoRecord(
        undoRecord: SketchTrimPowerUndoRecord,
        result: SketchTrimOperationResult,
        target: SketchTrimEntityRef,
    ): void {
        undoRecord.createdLineIds = [...result.createdEntityIds];
        const removedIds = new Set(result.removedEntityIds);
        undoRecord.removedLineSnapshots = undoRecord.removedLineSnapshots
            .filter((snapshot) => removedIds.has(snapshot.line.uuid));

        this.powerTrimUndoRecordStack.push(undoRecord);
        this.powerTrimUndoToken += 1;
        this.powerTrimMarkerSnapshotStack.push({
            undoToken: this.powerTrimUndoToken,
            worldPoint: this.resolvePowerMarkerWorldPoint(target, undoRecord.updatedLineSnapshots[0] ?? null),
        });
    }

    /**
     * 解析强力剪裁标记点世界坐标。
     * @param target 目标实体。
     * @param lineSnapshot 线段快照。
     * @returns 世界坐标。
     */
    private resolvePowerMarkerWorldPoint(
        target: SketchTrimEntityRef,
        lineSnapshot: SketchTrimUpdatedCurveSnapshot | null,
    ): [number, number, number] {
        if (target.hitPoint && target.hitPoint.length >= 3) {
            return [...target.hitPoint] as [number, number, number];
        }
        if (!lineSnapshot) {
            console.error(`[SketchTrimPowerModeService] 强力剪裁标记点解析失败：缺少更新快照，目标=${target.objectId}。`);
            return [0, 0, 0];
        }

        switch (lineSnapshot.curveKind) {
            case SketchTrimCurveKind.Line:
            case SketchTrimCurveKind.Arc:
            case SketchTrimCurveKind.Circle:
            case SketchTrimCurveKind.Ellipse:
                return this.resolveMidpointByPositions(lineSnapshot.startPosition, lineSnapshot.endPosition);
            case SketchTrimCurveKind.Parabola:
                return this.resolveTupleByVector(lineSnapshot.definition.apexWorld);
            case SketchTrimCurveKind.Spline:
            case SketchTrimCurveKind.StyleSpline:
                return this.resolveAveragePoint(
                    lineSnapshot.curvePointPositions.length > 0
                        ? lineSnapshot.curvePointPositions
                        : lineSnapshot.controlPointPositions,
                );
        }
    }

    /**
     * 根据两个世界坐标解析中点。
     * @param startPosition 起点坐标。
     * @param endPosition 终点坐标。
     * @returns 中点坐标。
     */
    private resolveMidpointByPositions(
        startPosition: [number, number, number],
        endPosition: [number, number, number],
    ): [number, number, number] {
        const [startX, startY, startZ] = startPosition;
        const [endX, endY, endZ] = endPosition;
        return [
            (startX + endX) * 0.5,
            (startY + endY) * 0.5,
            (startZ + endZ) * 0.5,
        ];
    }

    /**
     * 将 Three 向量转换为稳定的三元组坐标。
     * @param vector 世界坐标向量。
     * @returns 三元组坐标。
     */
    private resolveTupleByVector(vector: {x: number; y: number; z: number}): [number, number, number] {
        return [vector.x, vector.y, vector.z];
    }

    /**
     * 解析坐标列表的平均点。
     * @param positions 坐标列表。
     * @returns 平均点坐标；若列表为空则返回世界原点。
     */
    private resolveAveragePoint(positions: [number, number, number][]): [number, number, number] {
        if (positions.length === 0) {
            return [0, 0, 0];
        }

        const total = positions.reduce<[number, number, number]>(
            (sum, position) => [
                sum[0] + position[0],
                sum[1] + position[1],
                sum[2] + position[2],
            ],
            [0, 0, 0],
        );
        return [
            total[0] / positions.length,
            total[1] / positions.length,
            total[2] / positions.length,
        ];
    }
}
